import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Box from '@risk-smart/themed-cloudscape-components/box';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import Table from '@risksmart-app/components/src/table';
import type { IndicatorBand } from '@risksmart-app/domain/src/types/indicator-banding-config';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import PolarityBadge from '@/components/polarity-badge/PolarityBadge';
import { useGetLatestIndicatorBandingConfig } from '@/hooks/queries/indicator/useGetLatestIndicatorBandingConfig';

import { formatBandRule } from './formatBandRule';

type Props = {
  /**
   * Only the two fields this tab reads. Declared structurally rather than as
   * the indicator page's generated query row, so the tab does not depend on
   * that page's data source — it renders the same whether the page is served
   * by GraphQL or by tRPC.
   */
  indicator: { Id: string; Unit?: string | null };
};

/**
 * Read-only Bands tab (CUS-187, M1) — displays the indicator's current
 * banding configuration. Purely informational; the editor arrives in CUS-189.
 *
 * Rendered only when the caller (`useTabs`, via `shouldShowBandsTab`) decides
 * to mount it: `configurable_thresholds` on AND a number-type indicator. This
 * component doesn't re-check either condition, matching the sibling
 * `ScoreHistorySection` convention (RSP-6566) of leaving flag/type gating
 * entirely to the parent.
 *
 * Deliberately NOT coupled to `form_sections` — bands are per-indicator data,
 * not form configuration, so this surface is independent of the form-editor
 * work (product decision 2026-08-05, reverting the CUS-187 modal relocation).
 */
const Tab: FC<Props> = ({ indicator }) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'bands' });

  const { config, shape, loading, error } = useGetLatestIndicatorBandingConfig(
    indicator.Id
  );
  const bands = config?.bands ?? [];

  const columnDefinitions: TableProps.ColumnDefinition<IndicatorBand>[] = [
    {
      id: 'band',
      header: t('columns.band'),
      isRowHeader: true,
      cell: (band) => (
        <PolarityBadge
          label={band.label}
          colour={band.colour}
          polarity={band.polarity}
          severity={band.severity}
        />
      ),
    },
    {
      id: 'rule',
      header: t('columns.rule'),
      cell: (band) => formatBandRule(band, indicator.Unit),
    },
  ];

  if (!loading && error) {
    return <Alert type={'error'}>{t('error')}</Alert>;
  }

  return (
    <SpaceBetween direction={'vertical'} size={'m'}>
      {!loading && shape && <Box variant={'p'}>{t(`shape.${shape}`)}</Box>}
      <Table
        variant={'embedded'}
        loading={loading}
        loadingText={t('loading_message') ?? ''}
        items={bands}
        trackBy={'id'}
        columnDefinitions={columnDefinitions}
        sortingDisabled={true}
        empty={
          <Box textAlign={'center'} color={'inherit'}>
            {t('empty')}
          </Box>
        }
      />
    </SpaceBetween>
  );
};

export default Tab;
